// PR-I-83: helper puro para el header derecho de BookingCard cuando el bucket es `past`.
// El status llega como string libre desde el back (contrato en ../models.ts). Normalizamos
// a lowercase y aceptamos ambos spellings (cancelled/canceled, completed/finished) para
// tolerar variaciones sin romper el UI. NO hace fetch, no persiste nada, no expone datos
// sensibles: sólo transforma un BookingItem ya en memoria a { label, value } derivados.
//
// Seguridad:
// - `value` se deriva de `formatDateShortIntl2` (Intl.DateTimeFormat) o strings literales
//   controlados por esta función -> sin interpolación de HTML, seguro para {value} en JSX.
// - No se registran logs con PII (title, counterparty, etc.); sólo devolvemos strings de UI.

import { BookingItem } from '../models'
import { formatDateShortIntl2 } from '../../../helpers/FormatDate'

export interface PastLabel {
  /** Etiqueta descriptiva ("Cancelled On", "Finished On") o null si sólo hay un valor. */
  label: string | null
  /** Texto listo para render (fecha formateada o palabra clave). Puede ser ''. */
  value: string
}

/**
 * Devuelve la etiqueta apropiada para mostrar en el header derecho de una BookingCard
 * cuando el item vive en el bucket `past`.
 *
 * Contrato:
 * - status cancelled/canceled -> "Cancelled On <fecha>" o sólo "Cancelled" si no hay fecha.
 * - status completed/finished -> "Finished On <fecha>" o sólo "Finished" si no hay fecha.
 * - cualquier otro status -> fallback a la fecha si existe, o string vacío.
 *
 * Nota de contrato: el backend actual no expone `cancelledAt`; usamos `scheduledAt`
 * como fecha informativa. Cuando el back agregue `cancelledAt` / `completedAt` habrá
 * que ampliar `BookingItem` (models.ts) y preferirlos aquí sobre `scheduledAt`.
 */
export const getPastLabel = (item: BookingItem): PastLabel => {
  const status = (item.status || '').toLowerCase()

  if (status === 'cancelled' || status === 'canceled') {
    if (item.scheduledAt) {
      return { label: 'Cancelled On', value: formatDateShortIntl2(item.scheduledAt) }
    }
    return { label: null, value: 'Cancelled' }
  }

  if (status === 'completed' || status === 'finished') {
    if (item.scheduledAt) {
      return { label: 'Finished On', value: formatDateShortIntl2(item.scheduledAt) }
    }
    return { label: null, value: 'Finished' }
  }

  if (item.scheduledAt) {
    return { label: null, value: formatDateShortIntl2(item.scheduledAt) }
  }
  return { label: null, value: '' }
}
